import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import axiosInstance from '../utils/axiosInstance';
import {
  IMPORT_MAX_PHOTOS, prepareImportImage, sha256Hex,
} from '../utils/imageIntake';
import FilterIdentityCurveFields, {
  CurveCellFlags, IdentityCurveDraft, IdentityFieldName,
} from './FilterIdentityCurveFields';
import {
  AliasPreviewEntry, ConflictResolution, ConflictSibling, JobRecovery, ReviewedIdentity,
  aliasConsentLabel, aliasesToRecord, collapseNumber, correctionPrefill,
  curveStructuralErrors, identityKey, identityQuery, preselectResolution,
  reconcileCurveFlags, recoveryActions, reviewedIdentity, siblingMatch,
} from './labelProposalIdentity';
import Button from './ui/Button';
import ActionRow from './ui/ActionRow';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from './pilot-ui/collapsible';
import { Checkbox } from './pilot-ui/checkbox';
import { Input } from './pilot-ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './pilot-ui/select';
import { Textarea } from './pilot-ui/textarea';
import { RadioGroup, RadioGroupItem } from './pilot-ui/radio-group';

/**
 * Plan 107 Phase 4 - filter-label proposal review.
 * Plan 109 - admin manual entry into the same proposal queue.
 *
 * Pending curves (photo-harvested from the app, or hand-entered by an admin
 * off a datasheet) await operator ratification here. Nothing reaches the
 * live catalog until accept; manual entries publish as non-certified
 * 'manual', app photo entries as certified 'filter_label'. Shown only to
 * manage_filters users, inside FilterCatalogPage. This panel is the ONLY
 * catalog creation path — the direct-write Add Filter / Add Substitute
 * forms are retired.
 */

interface CurvePoint {
  resistance_iwc: number;
  cfm: number;
}

interface ProposalSummary {
  id: number;
  status: string;
  origin: string;
  created_at: string;
  submitting_uid: string;
  /** FS-863: the submitter's email, resolved by the API; null when no account answers. */
  submitting_email?: string | null;
  device_id: number | null;
  device_name: string | null;
  candidate_brand: string | null;
  candidate_brand_canon: string | null;
  candidate_nominal_size: string | null;
  candidate_depth_inches: number | null;
  candidate_merv: number | null;
  photo_count: number;
  point_count: number;
  sanity: { errors: string[]; warnings: string[] } | null;
  reviewer: string | null;
  reviewed_at: string | null;
}

/** FS-663: a catalog brand the reviewed spelling may have meant. */
interface BrandSuggestion {
  brand: string;
  brand_canon: string;
  sample_model: string;
  row_count: number;
}

/** FS-663: what `GET /label-proposals/:id?brand=…` says about one reviewed
 * identity. `key` is the server's `identity_key` echo — the panel keeps a
 * result only while the form still describes that key. */
interface IdentityCheckResult {
  key: string;
  identity_errors: Record<string, string>;
  /** The canon the reviewed brand resolves to today (null while invalid). */
  brand_canon: string | null;
  brand_known: boolean;
  brand_suggestions: BrandSuggestion[];
  conflicts: ConflictSibling[];
  /** The spellings an accept may record as aliases (F2). */
  alias_preview: AliasPreviewEntry[];
}

/** The check for the identity `key`. `result` is the last answer the panel
 * accepted (its own key inside) and is carried across pending/error so the
 * sibling list does not blink on every keystroke; it is painted as stale
 * until `status === 'ready'` for the current key. */
interface IdentityCheck {
  status: 'pending' | 'error' | 'ready';
  key: string;
  result: IdentityCheckResult | null;
}

/** Debounce for the re-query after an identity edit. The invalidation of
 * Accept and of the reviewer's choices is immediate; only the GET waits. */
const IDENTITY_CHECK_DEBOUNCE_MS = 300;

/** FS-663 `409 alias_conflict`: the spelling already maps elsewhere. The
 * API names it `alias_key`. */
interface AliasConflict {
  alias_key: string;
  origin: 'submitted' | 'reviewed';
  existing_canon: string;
  final_canon: string;
}

const IDENTITY_FIELD_NAMES: readonly IdentityFieldName[] = [
  'brand', 'model', 'nominal_size', 'depth_inches', 'merv', 'maedb_model_number',
];

interface ProposalDetail {
  /** The stored row, plus FS-863's `submitting_email` beside `submitting_uid`. */
  proposal: any;
  photo_proxy_urls: string[];
  sanity: { errors: string[]; warnings: string[]; point_count: number };
  /** FS-674: the import job whose proposal_id points at this proposal — a
   * corrected job (still `failed`) or a completed read. Null otherwise. */
  import_job?: { id: number; status: string; error_detail: string | null } | null;
  current_catalog_row: any | null;
  family_conflicts?: ConflictSibling[];
  family_comparisons: {
    catalog_id: number;
    nominal_size: string;
    model: string;
    area_scale_factor: number | null;
    scaled_curve: CurvePoint[] | null;
  }[];
  publication_preview: {
    would_create: boolean;
    derived: Record<string, number | null>;
    row: Record<string, unknown>;
  } | null;
}

/** Review form = the shared identity/curve draft + the review note.
 * `model` is the human display name (FS-504 D1) — blank falls back to the
 * server default (MAEDbS number, else "MERV {n}"). */
type AcceptForm = IdentityCurveDraft & { note: string };

/** Plan 109 manual-entry draft. All fields kept as strings while typing. */
type ManualForm = IdentityCurveDraft & { note: string };

const blankManualForm = (): ManualForm => ({
  brand: '', manufacturer: '', model: '', nominal_size: '',
  depth_inches: '1', merv: '', maedb_model_number: '', note: '',
  curve: [
    { resistance_iwc: '', cfm: '' },
    { resistance_iwc: '', cfm: '' },
  ],
});

/**
 * FS-863 — who submitted, by email. `submitting_uid` holds an email for a
 * console submission, a console account number when the token carried no
 * email, or the Firebase UID of the app account that sent the photos; the API
 * resolves each to `submitting_email`. A UID nobody answers to is said to be
 * an app account no longer on file, with the UID in the tooltip for tracing.
 */
export const submitterOf = (
  uid: string | null | undefined,
  email: string | null | undefined,
): { text: string; title?: string } => {
  const id = (uid ?? '').trim();
  const isAppUid = id !== '' && !id.includes('@') && !/^\d+$/.test(id);
  const title = isAppUid ? `Firebase UID: ${id}` : undefined;
  const resolved = (email ?? '').trim();
  if (resolved) return { text: resolved, title };
  if (id === '') return { text: '-' };
  if (id.includes('@')) return { text: id };
  if (!isAppUid) return { text: `a deleted account (#${id})` };
  return { text: 'an app account no longer on file', title };
};

const SubmitterName: React.FC<{ uid: string | null | undefined; email: string | null | undefined }> = (
  { uid, email },
) => {
  const s = submitterOf(uid, email);
  return <span data-testid="submitter" title={s.title}>{s.text}</span>;
};

const originLabel = (origin: string | null | undefined) =>
  origin === 'admin_photo' ? 'photo' :
    origin === 'admin_corrected' ? 'corrected' :
      origin === 'admin_manual' ? 'manual' : 'app';

/** The origin badge: manual entry is amber (non-certified); a photo read
 * and a hand correction of one (FS-674, certified under attestation) are
 * both purple like the app's photos. */
const originBadgeClass = (origin: string | null | undefined) =>
  (origin === 'admin_manual' ? 'border border-status-warning-border bg-status-warning text-status-warning-foreground' : 'bg-purple-100 text-purple-800');

/** Plan 109 Release B: photo-import job summary (extraction still pending).
 * FS-674: plus the retry lineage and the server's recovery verdicts. */
interface ImportJobSummary {
  id: number;
  status: string;
  created_at: string;
  submitting_uid: string;
  /** FS-863: the submitter's email, resolved by the API; null when no account answers. */
  submitting_email?: string | null;
  photo_count: number;
  note: string | null;
  error_detail: string | null;
  proposal_id: number | null;
  /** The failed job this one re-read (a "Retry read" child). */
  retry_of?: number | null;
  proposal_status?: string | null;
  /** The newest retry of this job. */
  retried_by?: { id: number; status: string; proposal_id: number | null } | null;
  /** What the server allows on this job right now. The panel renders it
   * and never infers availability from anything else. */
  recovery?: JobRecovery | null;
}

/** FS-665: one cell-level finding from the API's extraction diagnostics. */
interface DiagnosticFlag {
  code: 'unsupported_unit' | 'non_positive' | 'duplicate_resistance' | 'airflow_not_increasing';
  field: 'static_pressure' | 'airflow';
  previous_index?: number;
}

interface DiagnosticPoint {
  index: number;
  static_pressure: number | string | null;
  pressure_unit: number | string | null;
  airflow: number | string | null;
  airflow_unit: number | string | null;
  resistance_iwc: number | null;
  cfm: number | null;
  flags: DiagnosticFlag[];
}

interface ExtractionDiagnostics {
  points: DiagnosticPoint[];
  errors: string[];
  point_count: number;
}

/** FS-665: `GET /import-jobs/:id` — what the model read on a failed job. */
interface ImportJobDetail {
  job: {
    raw_extraction: {
      brand?: string | null;
      model_number?: string | null;
      nominal_size?: string | null;
      depth_inches?: number | null;
      merv?: number | null;
    } | null;
  };
  photo_proxy_urls: string[];
  extraction_diagnostics: ExtractionDiagnostics | null;
}

/** Loading and a failed request are distinct from "loaded, no extraction":
 * a failed fetch offers retry and never reads as a job without a read. */
type JobDetailState =
  | { state: 'loading' }
  | { state: 'error' }
  | { state: 'loaded'; detail: ImportJobDetail };

const FLAG_LABEL: Record<DiagnosticFlag['code'], string> = {
  unsupported_unit: 'unsupported_unit: the printed unit could not be converted',
  non_positive: 'non_positive: the converted value is not above zero',
  duplicate_resistance: 'duplicate_resistance: same converted resistance as another point',
  airflow_not_increasing: 'airflow_not_increasing: converted CFM is not above the previous point in resistance order',
};

const JOB_STATUS_BADGE: Record<string, string> = {
  queued: 'border border-status-neutral-border bg-status-neutral text-status-neutral-foreground',
  processing: 'border border-status-info-border bg-status-info text-status-info-foreground',
  completed: 'border border-status-success-border bg-status-success text-status-success-foreground',
  failed: 'border border-status-error-border bg-status-error text-status-error-foreground',
};

/** What a photo viewer reports about its image (FS-674): `loaded` only
 * from the <img>'s own load event, after the browser decoded it — bytes
 * arriving is not the photo showing; `failed` on a request error or an
 * image error (truncated data, a format the browser cannot decode), when
 * the photo shows as unable to load; `unmounted` from the effect cleanup,
 * when the displayed image's lifecycle ends — the detail closed, another
 * proposal opened, the panel collapsed — so nothing can remember a photo
 * that is no longer on the page. */
type PhotoLifecycle = 'loaded' | 'failed' | 'unmounted';

/** Proxy images require the JWT, so plain <img src> cannot load them.
 * `onSettled` (FS-674) reports the image's lifecycle (PhotoLifecycle) —
 * the attestation checkbox on a corrected proposal waits on every photo
 * being displayed and forgets any that leaves the page. */
const AuthedImage: React.FC<{
  url: string;
  alt: string;
  onSettled?: (state: PhotoLifecycle) => void;
}> = ({ url, alt, onSettled }) => {
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const settledRef = useRef(onSettled);
  settledRef.current = onSettled;

  useEffect(() => {
    let revoked: string | null = null;
    let cancelled = false;
    axiosInstance
      .get(url, { responseType: 'blob' })
      .then(res => {
        if (cancelled) return;
        revoked = URL.createObjectURL(res.data);
        setObjectUrl(revoked);
      })
      .catch(() => {
        if (cancelled) return;
        setFailed(true);
        settledRef.current?.('failed');
      });
    return () => {
      cancelled = true;
      if (revoked) URL.revokeObjectURL(revoked);
      // The image is gone from the page (or this URL's run is over):
      // whatever it reported no longer describes anything displayed.
      settledRef.current?.('unmounted');
    };
  }, [url]);

  if (failed) {
    return <div className="p-4 text-center text-muted-foreground text-sm">Unable to load photo</div>;
  }
  if (!objectUrl) {
    return <div className="p-4 text-center text-muted-foreground text-sm">Loading photo…</div>;
  }
  return (
    <a href={objectUrl} target="_blank" rel="noopener noreferrer">
      <img src={objectUrl} alt={alt} className="w-full max-h-96 object-contain bg-white"
        onLoad={() => settledRef.current?.('loaded')}
        onError={() => { setFailed(true); settledRef.current?.('failed'); }} />
    </a>
  );
};

const num = (s: string): number => Number(s);

const LabelProposalsPanel: React.FC<{
  baseUrl: string;
  onCatalogChanged: () => void;
}> = ({ baseUrl, onCatalogChanged }) => {
  const [statusFilter, setStatusFilter] = useState('pending');
  // Reviewed views default to the last 7 days so they don't accumulate old
  // entries; untick to see the full history. The pending view is exempt: a
  // pending proposal is actionable no matter its age, and the window (badge
  // included) used to silently hide any that aged past a week.
  const [recentOnly, setRecentOnly] = useState(true);
  const [proposals, setProposals] = useState<ProposalSummary[]>([]);
  const [total, setTotal] = useState(0);
  const [detail, setDetail] = useState<ProposalDetail | null>(null);
  const [form, setForm] = useState<AcceptForm | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [collapsed, setCollapsed] = useState(true);

  // FS-663: the catalog check for the identity the form describes. The
  // check answers with the occupied-family siblings (same brand, or another
  // brand sharing a model number), whether the brand exists, and per-field
  // identity errors; an accept 409 refreshes the same result. Accept stays
  // disabled until a check for the CURRENT identity key has answered
  // cleanly; the reviewer's choices below are cleared by any identity edit.
  const [identityCheck, setIdentityCheck] = useState<IdentityCheck | null>(null);
  const [resolution, setResolution] = useState<ConflictResolution | null>(null);
  const [confirmNewBrand, setConfirmNewBrand] = useState(false);
  const [recordAlias, setRecordAlias] = useState(true);
  const [identityTaken, setIdentityTaken] = useState<ConflictSibling | null>(null);
  const [aliasConflict, setAliasConflict] = useState<AliasConflict | null>(null);
  // The proposal + identity key the form describes right now; a response
  // for any other pair is dropped. Kept in a ref so an in-flight request
  // can read it on resolve.
  const identityKeyRef = useRef<string | null>(null);
  const checkTag = (proposalId: number, key: string) => `${proposalId}|${key}`;
  // The next scheduled check runs without the debounce (open, suggestion
  // click) — the edit was a whole decision, not a keystroke.
  const immediateCheckRef = useRef(false);

  // Plan 109 manual entry. One idempotency UUID per draft, generated when
  // the form opens and retained across submit retries so the server can
  // return the original proposal instead of creating a duplicate.
  const [manual, setManual] = useState<ManualForm | null>(null);
  const [manualKey, setManualKey] = useState<string | null>(null);
  const [manualBusy, setManualBusy] = useState(false);
  // FS-674 Correct by hand: the same form, prefilled from a failed job's
  // read and posted to that job's /correct. `manualTarget` is the job;
  // `curveFlags` is the per-row cell state from the read's diagnostics
  // (kept in step with the rows by reconcileCurveFlags; the identity-only
  // `fieldErrors` never carries it).
  const [manualTarget, setManualTarget] = useState<{ jobId: number } | null>(null);
  const [curveFlags, setCurveFlags] = useState<CurveCellFlags[]>([]);

  // FS-674 Retry read. One idempotency key per INTENDED retry: minted on
  // the first click and kept through timeouts and unknown outcomes (a
  // second click re-sends it); discarded only after a success or a
  // definitive 4xx. Per job, so two rows never share a key.
  const retryKeys = useRef<Record<number, string>>({});
  const [retryBusy, setRetryBusy] = useState<number | null>(null);
  const [retryErrors, setRetryErrors] = useState<Record<number, string>>({});

  // FS-674 §3: the accepting admin's attestation on an admin_corrected
  // proposal. It belongs to one proposal's detail view: enabled only once
  // every photo of that proposal has loaded, reset by any identity or
  // curve edit and by closing or switching the detail.
  const [verifiedAgainstPhotos, setVerifiedAgainstPhotos] = useState(false);
  const [photoLoad, setPhotoLoad] = useState<Record<string, 'loaded' | 'failed'>>({});

  /** A detail photo's lifecycle report. Readiness is only ever what the
   * images on the page say: `loaded` / `failed` are recorded for that URL,
   * `unmounted` drops it. Any move out of `loaded` — a failure, or the
   * image leaving the page (close, switch, panel collapse) — unticks the
   * attestation, which vouched for photos that were displayed. */
  const onDetailPhoto = (url: string, state: PhotoLifecycle) => {
    setPhotoLoad(m => {
      if (state === 'unmounted') {
        if (!(url in m)) return m;
        const { [url]: _gone, ...rest } = m;
        return rest;
      }
      return { ...m, [url]: state };
    });
    if (state !== 'loaded') setVerifiedAgainstPhotos(false);
  };

  // Plan 109 Release B photo-import intake. Same per-draft idempotency-key
  // discipline as manual entry.
  const [importOpen, setImportOpen] = useState(false);
  const [importFiles, setImportFiles] = useState<File[]>([]);
  const [importNote, setImportNote] = useState('');
  const [importKey, setImportKey] = useState<string | null>(null);
  const [importBusy, setImportBusy] = useState(false);
  const [jobs, setJobs] = useState<ImportJobSummary[]>([]);
  const [jobPhotos, setJobPhotos] = useState<{ id: number; urls: string[] } | null>(null);
  // FS-665: per-job detail (what the model read), fetched once on the first
  // expand of a failed job and kept apart from the 5-second summary polling.
  const [jobDetails, setJobDetails] = useState<Record<number, JobDetailState>>({});

  const proposalsUrl = `${baseUrl}/label-proposals`;

  const fetchProposals = useCallback(async () => {
    try {
      const params = new URLSearchParams({ status: statusFilter, limit: '50' });
      if (recentOnly && statusFilter !== 'pending') {
        params.set('since',
          new Date(Date.now() - 7 * 24 * 3600 * 1000).toISOString());
      }
      const res = await axiosInstance.get(`${proposalsUrl}?${params.toString()}`);
      setProposals(res.data.data);
      setTotal(res.data.total);
    } catch {
      // Endpoint may not be deployed yet; keep the panel quiet.
      setProposals([]);
      setTotal(0);
    }
  }, [proposalsUrl, statusFilter, recentOnly]);

  useEffect(() => { fetchProposals(); }, [fetchProposals]);

  // FS-663: every reviewer decision is about one identity. An edit to any of
  // the six identity fields (or a suggestion click) forgets them all,
  // synchronously, in the same batch as the form change.
  const forgetIdentityDecisions = () => {
    setResolution(null);
    setConfirmNewBrand(false);
    setRecordAlias(true);
    setIdentityTaken(null);
    setAliasConflict(null);
  };

  const resetIdentityCheck = () => {
    identityKeyRef.current = null;
    setIdentityCheck(null);
    forgetIdentityDecisions();
  };

  /** The keyed check. Issued for `key`; on resolve, a response is kept
   * only if `key` is still the current identity AND the server's echo says
   * it computed for that same key — otherwise it is dropped on the floor. */
  const runIdentityCheck = async (
    proposalId: number, identity: ReviewedIdentity, key: string,
  ) => {
    setIdentityCheck(prev => ({
      status: 'pending', key, result: prev?.result ?? null,
    }));
    let next: IdentityCheck;
    try {
      const res = await axiosInstance.get(
        `${proposalsUrl}/${proposalId}?${identityQuery(identity)}`);
      if (identityKeyRef.current !== checkTag(proposalId, key)) return;
      const d = res.data ?? {};
      if (typeof d.identity_key !== 'string') {
        // The API does not speak this contract yet: never enable Accept
        // on an answer that cannot be matched to the question.
        next = { status: 'error', key, result: null };
      } else if (d.identity_key !== key) {
        return;
      } else {
        const result: IdentityCheckResult = {
          key,
          identity_errors: d.identity_errors ?? {},
          brand_canon: d.identity?.brand_canon ?? null,
          brand_known: d.brand_known !== false,
          brand_suggestions: d.brand_suggestions ?? [],
          conflicts: d.family_conflicts ?? [],
          alias_preview: d.alias_preview ?? [],
        };
        next = { status: 'ready', key, result };
        setResolution(preselectResolution(
          result.conflicts, identity.maedb_model_number || null));
      }
    } catch {
      if (identityKeyRef.current !== checkTag(proposalId, key)) return;
      next = { status: 'error', key, result: null };
    }
    setIdentityCheck(prev => (next.result
      ? next : { ...next, result: prev?.result ?? null }));
  };

  // The identity the review form describes, and its key. Non-identity
  // edits (manufacturer, note, curve) leave the key alone.
  const currentIdentity = form ? reviewedIdentity(form) : null;
  const currentKey = currentIdentity ? identityKey(currentIdentity) : null;

  // Schedule a check whenever the key changes or a detail (re)opens. The
  // ref moves at once, so a response for the previous key that lands in the
  // debounce window is already stale; the GET itself waits out the debounce.
  // `identity` is read here rather than listed as a dependency: it changes
  // exactly when `currentKey` does.
  useEffect(() => {
    if (!detail || detail.proposal.status !== 'pending' ||
        !currentIdentity || currentKey == null) {
      identityKeyRef.current = null;
      return undefined;
    }
    const identity = currentIdentity;
    const proposalId: number = detail.proposal.id;
    identityKeyRef.current = checkTag(proposalId, currentKey);
    const delay = immediateCheckRef.current ? 0 : IDENTITY_CHECK_DEBOUNCE_MS;
    immediateCheckRef.current = false;
    const t = setTimeout(() => runIdentityCheck(proposalId, identity, currentKey), delay);
    return () => clearTimeout(t);
  }, [detail, currentKey]);

  const openDetail = async (id: number) => {
    setError('');
    try {
      const res = await axiosInstance.get(`${proposalsUrl}/${id}`);
      const d: ProposalDetail = res.data;
      // The plain GET's family_conflicts describe the stored candidate; the
      // keyed check the effect issues for the form's identity governs.
      resetIdentityCheck();
      immediateCheckRef.current = true;
      // FS-674: every opening starts unattested. Photo readiness belongs
      // to the images actually on the page: entries for any OTHER proposal
      // are dropped (those images unmount as the detail changes and the new
      // ones report themselves), while entries for this proposal's own
      // photos are kept — reopening the proposal that is already open
      // leaves its <AuthedImage>s mounted (same keys, same URLs), and a
      // mounted image never reports twice.
      const ownPhotos = `${proposalsUrl}/${id}/photos/`;
      setVerifiedAgainstPhotos(false);
      setPhotoLoad(m => Object.fromEntries(
        Object.entries(m).filter(([u]) => u.startsWith(ownPhotos))));
      setDetail(d);
      const p = d.proposal;
      setForm({
        brand: p.candidate_brand ?? '',
        manufacturer: '',
        // Human display name (FS-504): the submitter's typed name, else the
        // server's preview default. Editable; blank re-derives on accept.
        model: p.candidate_model
          ?? (d.publication_preview?.row?.model != null
            ? String(d.publication_preview.row.model) : ''),
        nominal_size: p.candidate_nominal_size ?? '',
        depth_inches: p.candidate_depth_inches != null ? String(p.candidate_depth_inches) : '1',
        merv: p.candidate_merv != null ? String(p.candidate_merv) : '',
        maedb_model_number: p.candidate_maedb_model_number ?? '',
        // Review note starts empty — the submitter's source note is shown
        // read-only instead. Prefilling it here polluted reject_reason, and
        // accept keeps the source note server-side when no new note is typed.
        note: '',
        curve: (p.candidate_curve ?? []).map((pt: CurvePoint) => ({
          resistance_iwc: String(pt.resistance_iwc),
          cfm: String(pt.cfm),
        })),
      });
    } catch (err: unknown) {
      const axiosError = err as { response?: { data?: { message?: string } } };
      setError(axiosError?.response?.data?.message ?? 'Failed to load proposal');
    }
  };

  const closeDetail = () => {
    setDetail(null); setForm(null); setError('');
    resetIdentityCheck();
    setVerifiedAgainstPhotos(false);
    setPhotoLoad({});
  };

  const setFormField = (field: keyof AcceptForm, value: string) =>
    setForm(f => (f ? { ...f, [field]: value } : f));

  /** The review form's identity/curve block. An identity edit forgets the
   * reviewer's decisions in the same batch; the effect above re-queries. */
  const editReviewForm = (next: IdentityCurveDraft) => {
    const identityChanged = form != null &&
      identityKey(reviewedIdentity(next)) !== identityKey(reviewedIdentity(form));
    if (identityChanged) forgetIdentityDecisions();
    // FS-674 §3: the attestation vouches for the exact identity and every
    // curve value as they read when it was ticked; any change to either
    // unticks it. Manufacturer and note are neither.
    const curveChanged = form != null &&
      JSON.stringify(next.curve) !== JSON.stringify(form.curve);
    if (identityChanged || curveChanged) setVerifiedAgainstPhotos(false);
    setForm(f => (f ? { ...f, ...next } : f));
  };

  /** A whole-decision identity change (suggestion click, "clear the
   * number"): same invalidation, but the re-query does not wait. */
  const replaceIdentityField = (field: IdentityFieldName, value: string) => {
    if (!form) return;
    immediateCheckRef.current = true;
    editReviewForm({ ...form, [field]: value });
  };

  const patchCheckResult = (fn: (r: IdentityCheckResult) => IdentityCheckResult) =>
    setIdentityCheck(prev => (prev?.result ? { ...prev, result: fn(prev.result) } : prev));

  // ── Plan 109: manual entry ────────────────────────────────────────────────

  // Photo-import job list (Release B intake). The endpoint may not be
  // deployed yet; stay quiet on failure like fetchProposals.
  const fetchJobs = useCallback(async () => {
    try {
      const res = await axiosInstance.get(`${baseUrl}/import-jobs?limit=20`);
      setJobs(res.data?.data ?? []);
    } catch {
      setJobs([]);
    }
  }, [baseUrl]);

  useEffect(() => {
    if (!collapsed) fetchJobs();
  }, [collapsed, fetchJobs]);

  // Live status while extraction runs: poll jobs (and proposals, which gain
  // a row when a job completes) until nothing is queued or processing.
  // Bounded: after 5 minutes with no status change the pipeline is stuck,
  // not slow — stop hammering the API and surface a manual Refresh.
  const hasActiveJobs = jobs.some(j => j.status === 'queued' || j.status === 'processing');
  const [pollStalled, setPollStalled] = useState(false);
  const stallTicks = useRef(0);
  const jobsSnapshot = useRef('');

  useEffect(() => {
    const snap = JSON.stringify(jobs.map(j => [j.id, j.status]));
    if (snap !== jobsSnapshot.current) {
      jobsSnapshot.current = snap;
      stallTicks.current = 0;
      setPollStalled(false);
    }
  }, [jobs]);

  useEffect(() => {
    if (collapsed || !hasActiveJobs || pollStalled) return undefined;
    const t = setInterval(() => {
      stallTicks.current += 1;
      if (stallTicks.current > 60) { setPollStalled(true); return; }
      fetchJobs();
      fetchProposals();
    }, 5_000);
    return () => clearInterval(t);
  }, [collapsed, hasActiveJobs, pollStalled, fetchJobs, fetchProposals]);

  const resumePolling = () => {
    stallTicks.current = 0;
    setPollStalled(false);
    fetchJobs();
    fetchProposals();
  };

  const openManual = () => {
    setCollapsed(false);
    closeDetail();
    setImportOpen(false);
    setManual(blankManualForm());
    setManualTarget(null);
    setCurveFlags([]);
    setManualKey(crypto.randomUUID());
  };

  const closeManual = () => {
    setManual(null);
    setManualKey(null);
    setManualTarget(null);
    setCurveFlags([]);
    setError('');
  };

  /** The identity/curve block of the manual form. The read's cell flags
   * follow their rows: an edited cell loses its flag, a removed row takes
   * its flags along, an added row has none. */
  const editManualForm = (next: IdentityCurveDraft) => {
    if (manual) setCurveFlags(f => reconcileCurveFlags(manual.curve, next.curve, f));
    setManual(m => (m ? { ...m, ...next } : m));
  };

  // FS-674: the structural curve rules, run locally on a resistance-sorted
  // copy exactly as the API's validateReviewCurve does. Submit is disabled
  // while they fail — the untouched job-21 draft cannot be posted (the API
  // rejects it with 400 independently). Range warnings never disable it.
  const manualCurveErrors = manual ? curveStructuralErrors(manual.curve) : [];
  const manualCurveTouched = manual
    ? manual.curve.some(pt => pt.resistance_iwc.trim() !== '' || pt.cfm.trim() !== '')
    : false;

  const setManualField = (field: keyof ManualForm, value: string) =>
    setManual(m => (m ? { ...m, [field]: value } : m));

  const submitManual = async () => {
    if (!manual || !manualKey || manualCurveErrors.length > 0) return;
    setManualBusy(true);
    setError('');
    try {
      // FS-674: a correction posts the same body to the failed job's
      // /correct, never to the manual endpoint — the proposal keeps the
      // job's photos as evidence and the job links to it.
      const target = manualTarget
        ? `${baseUrl}/import-jobs/${manualTarget.jobId}/correct`
        : `${proposalsUrl}/from-manual`;
      const res = await axiosInstance.post(target, {
        idempotency_key: manualKey,
        candidate_brand: manual.brand.trim(),
        candidate_model: manual.model.trim() || null,
        candidate_nominal_size: manual.nominal_size.trim(),
        candidate_depth_inches: num(manual.depth_inches),
        candidate_merv: num(manual.merv),
        candidate_maedb_model_number: manual.maedb_model_number.trim() || null,
        candidate_curve: manual.curve.map(pt => ({
          resistance_iwc: num(pt.resistance_iwc),
          cfm: num(pt.cfm),
        })),
        note: manual.note.trim() || null,
      });
      // Open the created proposal so the server-computed family comparison
      // and publication preview are immediately visible for review.
      const corrected = manualTarget != null;
      setManual(null);
      setManualKey(null);
      setManualTarget(null);
      setCurveFlags([]);
      // The corrected job's row now links the proposal and offers neither
      // button — the server says so on the next list.
      if (corrected) await fetchJobs();
      await fetchProposals();
      await openDetail(res.data.id);
    } catch (err: unknown) {
      // Keep the draft AND its idempotency key: a retry of the same draft
      // must reuse the key so the server never creates a duplicate.
      const axiosError = err as { response?: { data?: { message?: string } } };
      setError(axiosError?.response?.data?.message ?? 'Failed to submit manual entry');
    } finally {
      setManualBusy(false);
    }
  };

  // ── FS-674: retry read / correct by hand on an expanded failed job ────────

  /** Retry read. The server copies the evidence from the job row; the
   * client sends only the key. A success (or a `created: false` replay)
   * puts the queued child in the list at once — which restarts the poll
   * loop even when every job was terminal — and then refreshes the list
   * so the parent's lineage and verdicts are the server's. */
  const startRetry = async (job: ImportJobSummary) => {
    const key = retryKeys.current[job.id] ?? (retryKeys.current[job.id] = crypto.randomUUID());
    setRetryBusy(job.id);
    setRetryErrors(e => ({ ...e, [job.id]: '' }));
    try {
      const res = await axiosInstance.post(`${baseUrl}/import-jobs/${job.id}/retry`,
        { idempotency_key: key });
      delete retryKeys.current[job.id];
      const childId: number = res.data?.id;
      if (childId != null) {
        const child: ImportJobSummary = {
          id: childId,
          status: 'queued',
          created_at: new Date().toISOString(),
          submitting_uid: '',
          photo_count: job.photo_count,
          note: job.note,
          error_detail: null,
          proposal_id: null,
          retry_of: job.id,
          proposal_status: null,
          retried_by: null,
          // No verdict until the server sends one.
          recovery: null,
        };
        setJobs(prev => [child, ...prev.filter(j => j.id !== childId)]);
      }
      await fetchJobs();
    } catch (err: unknown) {
      const axiosError = err as {
        response?: { status?: number; data?: { message?: string } }; message?: string;
      };
      const status = axiosError?.response?.status;
      // A definitive answer ends this intended retry; a timeout or an
      // unknown outcome keeps the key for the next click.
      if (status != null && status >= 400 && status < 500) delete retryKeys.current[job.id];
      setRetryErrors(e => ({
        ...e,
        [job.id]: axiosError?.response?.data?.message
          ?? axiosError?.message ?? 'Retry failed',
      }));
    } finally {
      setRetryBusy(null);
    }
  };

  /** Correct by hand: the manual form, prefilled from the job's read and
   * its diagnostics, aimed at the job's /correct. */
  const openCorrection = (job: ImportJobSummary) => {
    const state = jobDetails[job.id];
    if (!state || state.state !== 'loaded') return;
    const { draft, flags } = correctionPrefill(
      state.detail.job.raw_extraction, state.detail.extraction_diagnostics);
    setCollapsed(false);
    closeDetail();
    setImportOpen(false);
    setManual({ ...draft, note: '' });
    setCurveFlags(flags);
    setManualTarget({ jobId: job.id });
    setManualKey(crypto.randomUUID());
    setError('');
  };

  /** Accept. Only reachable while the check for the current identity has
   * answered cleanly (the button is disabled otherwise). `recordAliasNow`
   * is the alias-conflict escape hatch: the choice and the post in one
   * click. */
  const accept = async (recordAliasNow: boolean = recordAlias) => {
    if (!detail || !form || !acceptEnabled || currentKey == null) return;
    // F5: the identity this POST decides on. The fields are frozen while it
    // is in flight; should the identity still move (close, reopen), whatever
    // failure it brings back — a 409's state, an error message — answers a
    // question no longer asked and is discarded. A success still closes the
    // detail: the proposal is no longer pending.
    const issuedTag = checkTag(detail.proposal.id, currentKey);
    setRecordAlias(recordAliasNow);
    setBusy(true);
    setError('');
    try {
      await axiosInstance.post(`${proposalsUrl}/${detail.proposal.id}/accept`, {
        brand: form.brand.trim(),
        manufacturer: form.manufacturer.trim() || null,
        model: form.model.trim() || null,
        nominal_size: form.nominal_size.trim(),
        depth_inches: num(form.depth_inches),
        merv: num(form.merv),
        maedb_model_number: form.maedb_model_number.trim() || null,
        curve: form.curve.map(pt => ({
          resistance_iwc: num(pt.resistance_iwc),
          cfm: num(pt.cfm),
        })),
        note: form.note.trim() || null,
        ...(resolution ? { conflict_resolution: resolution } : {}),
        ...(confirmNewBrand ? { confirm_new_brand: true } : {}),
        record_alias: recordAliasNow,
        // FS-674 §3: the attestation, only on a corrected proposal (the
        // button is disabled until it is ticked). The server judges the
        // requirement on its own row, never on this.
        ...(attestationRequired ? { verified_against_photos: true } : {}),
      });
      closeDetail();
      fetchProposals();
      // A review decision changes what its job offers (a rejection
      // reopens Retry read on a corrected job).
      fetchJobs();
      onCatalogChanged();
    } catch (err: unknown) {
      if (identityKeyRef.current !== issuedTag) return;
      const axiosError = err as {
        response?: {
          data?: {
            message?: string; error?: string; siblings?: ConflictSibling[];
            brand_suggestions?: BrandSuggestion[]; row?: ConflictSibling;
            alias_key?: string; origin?: 'submitted' | 'reviewed';
            existing_canon?: string; final_canon?: string;
          };
        };
      };
      // Each 409 opens the state it names instead of a dead end (the
      // explicit server message still shows above it). They all describe
      // the identity that was posted, which the guard above says is still
      // the current one.
      const data = axiosError?.response?.data;
      const typed = form.maedb_model_number.trim() || null;
      if (data?.error === 'catalog_conflict' && data.siblings) {
        const siblings = data.siblings;
        patchCheckResult(r => ({ ...r, conflicts: siblings }));
        setResolution(preselectResolution(siblings, typed));
      } else if (data?.error === 'unknown_brand') {
        const suggestions = data.brand_suggestions;
        patchCheckResult(r => ({
          ...r, brand_known: false,
          brand_suggestions: suggestions ?? r.brand_suggestions,
        }));
      } else if (data?.error === 'identity_taken' && data.row) {
        // The row arrives as a full sibling, annotated against the
        // reviewed identity (match_reason, matched_token, matched_from,
        // exact_identity, brand_canon).
        setIdentityTaken(data.row);
      } else if (data?.error === 'alias_conflict' && data.alias_key) {
        setAliasConflict({
          alias_key: data.alias_key,
          origin: data.origin ?? 'submitted',
          existing_canon: data.existing_canon ?? '',
          final_canon: data.final_canon ?? '',
        });
      }
      setError(data?.message ?? 'Accept failed');
    } finally {
      setBusy(false);
    }
  };

  /** `409 identity_taken`: the reviewer takes the named row as the target.
   * It joins the sibling list if the check did not already list it. */
  const chooseTakenRow = () => {
    if (!identityTaken) return;
    const row = identityTaken;
    patchCheckResult(r => (r.conflicts.some(s => s.id === row.id)
      ? r : { ...r, conflicts: [...r.conflicts, row] }));
    setResolution({ action: 'update_existing', catalog_id: row.id });
    setIdentityTaken(null);
    setError('');
  };

  // What the current identity's check says, if it has answered for THIS
  // key. Anything else (no check yet, pending, error, or a result for an
  // identity the reviewer has since edited) keeps Accept off.
  const check = identityCheck && identityCheck.key === currentKey ? identityCheck : null;
  const freshResult = check?.status === 'ready' ? check.result : null;
  const identityErrors = freshResult?.identity_errors ?? {};
  const identityErrorEntries = Object.entries(identityErrors);
  // FS-674 §3: on a corrected proposal the attestation is part of being
  // ready to accept. Its checkbox waits for every photo of THIS proposal.
  const attestationRequired = detail?.proposal?.origin === 'admin_corrected';
  const detailPhotoUrls: string[] = detail
    ? detail.photo_proxy_urls.map((_, i) => `${proposalsUrl}/${detail.proposal.id}/photos/${i}`)
    : [];
  const anyPhotoFailed = detailPhotoUrls.some(u => photoLoad[u] === 'failed');
  const allPhotosLoaded = detailPhotoUrls.every(u => photoLoad[u] === 'loaded');
  // Accept on a corrected proposal needs the attestation AND every photo of
  // this proposal displayed right now — a pending or failed photo takes
  // Accept away whatever the checkbox says (the accept handler is gated on
  // this same value).
  const photosReady = allPhotosLoaded && !anyPhotoFailed;
  const acceptEnabled = !busy && freshResult != null && identityErrorEntries.length === 0 &&
    (!attestationRequired || (verifiedAgainstPhotos && photosReady));
  const acceptDisabledReason = busy
    ? 'The review action is in progress.'
    : freshResult == null
      ? 'Wait for the catalog identity check to finish.'
      : identityErrorEntries.length > 0
        ? 'Accept waits for valid identity values.'
        : attestationRequired && !photosReady
          ? 'Wait for every source photo to load.'
          : attestationRequired && !verifiedAgainstPhotos
            ? 'Check the identity and curve against the photos, then tick the attestation.'
            : null;
  // The sibling list stays painted from the last answer while a re-check
  // is pending, marked stale and not selectable.
  const shownResult = identityCheck?.result ?? null;
  const conflicts = shownResult?.conflicts ?? [];
  const conflictsFresh = freshResult != null && shownResult === freshResult;
  // F2: the aliases this accept would record, whatever found the target.
  // The final canon is the chosen update target's, else the reviewed
  // brand's; the box lists every spelling that would come to mean it.
  const chosenTarget = resolution?.action === 'update_existing'
    ? conflicts.find(s => s.id === resolution.catalog_id) : undefined;
  const finalCanon = chosenTarget?.brand_canon ?? freshResult?.brand_canon ?? null;
  const finalBrand = chosenTarget?.brand ?? (form?.brand.trim() ?? '');
  const aliasesPending = freshResult
    ? aliasesToRecord(freshResult.alias_preview, finalCanon) : [];
  const fieldErrors: Partial<Record<IdentityFieldName, string>> = {};
  const otherIdentityErrors: string[] = [];
  for (const [field, message] of identityErrorEntries) {
    if ((IDENTITY_FIELD_NAMES as readonly string[]).includes(field)) {
      fieldErrors[field as IdentityFieldName] = message;
    } else {
      otherIdentityErrors.push(`${field}: ${message}`);
    }
  }

  const reject = async () => {
    if (!detail || !form) return;
    if (!form.note.trim()) {
      setError('A note explaining the rejection is required.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      await axiosInstance.post(`${proposalsUrl}/${detail.proposal.id}/reject`,
        { note: form.note.trim() });
      closeDetail();
      fetchProposals();
      fetchJobs();
    } catch (err: unknown) {
      const axiosError = err as { response?: { data?: { message?: string } } };
      setError(axiosError?.response?.data?.message ?? 'Reject failed');
    } finally {
      setBusy(false);
    }
  };

  // ── Plan 109 Release B: photo-import intake ───────────────────────────────

  const openImport = () => {
    setCollapsed(false);
    closeDetail();
    setManual(null);
    setManualKey(null);
    setImportOpen(true);
    setImportFiles([]);
    setImportNote('');
    setImportKey(crypto.randomUUID());
  };

  const closeImport = () => {
    setImportOpen(false);
    setImportFiles([]);
    setImportNote('');
    setImportKey(null);
    setError('');
  };

  const addImportFiles = (incoming: FileList | File[]) => {
    const images = Array.from(incoming).filter(f => f.type.startsWith('image/'));
    setImportFiles(prev => [...prev, ...images].slice(0, IMPORT_MAX_PHOTOS));
  };

  const removeImportFile = (idx: number) =>
    setImportFiles(prev => prev.filter((_, i) => i !== idx));

  const submitImport = async () => {
    if (importFiles.length === 0 || !importKey) return;
    setImportBusy(true);
    setError('');
    try {
      // 1. Resize + hash locally (≤2576 px long edge, JPEG).
      const prepared: { blob: Blob; sha: string }[] = [];
      for (const f of importFiles) {
        const blob = await prepareImportImage(f);
        prepared.push({ blob, sha: await sha256Hex(blob) });
      }
      // 2. Mint session-scoped presigned PUTs; bytes go straight to S3 —
      //    never through CloudFront (WAF rejects >8 KB bodies).
      const presign = await axiosInstance.post(`${baseUrl}/import-uploads`, {
        files: prepared.map(p => ({
          content_type: 'image/jpeg',
          size_bytes: p.blob.size,
          sha256: p.sha,
        })),
      });
      const { upload_session, uploads } = presign.data;
      for (let i = 0; i < uploads.length; i++) {
        await axios.put(uploads[i].presigned_url, prepared[i].blob, {
          headers: { 'Content-Type': uploads[i].content_type },
        });
      }
      // 3. Submit the job (202 + id; stays queued until the extraction
      //    worker ships).
      await axiosInstance.post(`${baseUrl}/import-jobs`, {
        idempotency_key: importKey,
        upload_session,
        photos: uploads.map((u: { key: string }, i: number) => ({
          key: u.key,
          sha256: prepared[i].sha,
        })),
        note: importNote.trim() || null,
      });
      closeImport();
      await fetchJobs();
    } catch (err: unknown) {
      // Keep the draft AND its idempotency key so a retry can't duplicate.
      const axiosError = err as { response?: { data?: { message?: string } } };
      const message = axiosError?.response?.data?.message
        ?? (err instanceof Error ? err.message : 'Photo import failed');
      setError(message);
    } finally {
      setImportBusy(false);
    }
  };

  // Same catalog base as the list call: axiosInstance has no baseURL, so an
  // origin-relative path would hit the SPA and get index.html back.
  const fetchJobDetail = async (jobId: number) => {
    setJobDetails(d => ({ ...d, [jobId]: { state: 'loading' } }));
    try {
      const res = await axiosInstance.get(`${baseUrl}/import-jobs/${jobId}`);
      setJobDetails(d => ({ ...d, [jobId]: { state: 'loaded', detail: res.data } }));
    } catch {
      setJobDetails(d => ({ ...d, [jobId]: { state: 'error' } }));
    }
  };

  const toggleJobPhotos = async (job: ImportJobSummary) => {
    const jobId = job.id;
    if (jobPhotos?.id === jobId) { setJobPhotos(null); return; }
    setJobPhotos({
      id: jobId,
      urls: Array.from({ length: job.photo_count }, (_, i) =>
        `${baseUrl}/import-jobs/${jobId}/photos/${i}`),
    });
    // Only a failed job has a read worth explaining; fetch it once.
    if (job.status === 'failed' && !jobDetails[jobId]) fetchJobDetail(jobId);
  };

  // FS-665: "What the model read" for an expanded failed job. Renders only
  // what the API computed — the printed cells, and the callback's own
  // findings mapped onto them. A flagged cell is red; the airflow cell an
  // `airflow_not_increasing` flag was compared against is amber-outlined.
  const renderJobRead = (jobId: number) => {
    const state = jobDetails[jobId];
    if (!state) return null;
    if (state.state === 'loading') {
      return <p className="text-xs text-muted-foreground mb-2">Loading what the model read…</p>;
    }
    if (state.state === 'error') {
      return (
        <p className="text-xs text-red-700 mb-2">
          Couldn't load what the model read.{' '}
          <Button variant="link" onClick={() => fetchJobDetail(jobId)}
            title="Load what the label reader read from this job's photos again.">
            Retry
          </Button>
        </p>
      );
    }
    const diag = state.detail.extraction_diagnostics;
    if (!diag) return null;
    const identity = state.detail.job.raw_extraction ?? {};
    const show = (v: unknown) => (v == null || v === '' ? '—' : String(v));
    const identityFields: [string, string, unknown][] = [
      ['brand', 'Brand', identity.brand],
      ['model_number', 'Model number', identity.model_number],
      ['nominal_size', 'Nominal size', identity.nominal_size],
      ['depth_inches', 'Depth (in)', identity.depth_inches],
      ['merv', 'MERV', identity.merv],
    ];
    const comparedAgainst = new Map<number, number>();
    for (const p of diag.points) {
      for (const f of p.flags) {
        if (f.code === 'airflow_not_increasing' && f.previous_index != null) {
          comparedAgainst.set(f.previous_index, p.index);
        }
      }
    }
    const cell = (p: DiagnosticPoint, field: DiagnosticFlag['field']) => {
      const flags = p.flags.filter(f => f.field === field);
      const predecessorOf = field === 'airflow' ? comparedAgainst.get(p.index) : undefined;
      const titles = flags.map(f => FLAG_LABEL[f.code] +
        (f.previous_index != null ? ` (point ${f.previous_index + 1})` : ''));
      if (predecessorOf != null) {
        titles.push(`airflow_not_increasing: point ${predecessorOf + 1} was compared against this cell`);
      }
      const value = field === 'airflow' ? p.airflow : p.static_pressure;
      const unit = field === 'airflow' ? p.airflow_unit : p.pressure_unit;
      return (
        <td key={`${field}-${p.index}`}
          data-testid={`diag-${field}-${p.index}`}
          title={titles.length ? titles.join('; ') : undefined}
          className={`px-2 py-1 border text-center whitespace-nowrap ${
            flags.length ? 'bg-status-error text-status-error-foreground ring-2 ring-status-error-border' : ''} ${
            predecessorOf != null ? 'outline-solid outline-2 -outline-offset-2 outline-status-warning-border' : ''}`}>
          {show(value)} {show(unit)}
        </td>
      );
    };
    return (
      <div className="mb-2 border rounded-md p-2 bg-gray-50">
        <h5 className="text-xs font-bold mb-1">What the model read</h5>
        <dl className="flex flex-wrap gap-x-4 gap-y-0.5 text-xs mb-1">
          {identityFields.map(([key, label, v]) => (
            <div key={key}>
              <dt className="inline text-muted-foreground">{label}: </dt>
              <dd className="inline" data-testid={`diag-identity-${key}`}>{show(v)}</dd>
            </div>
          ))}
        </dl>
        {diag.points.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="table-auto border-collapse border border-border text-xs">
              <tbody>
                <tr>
                  <th className="px-2 py-1 border text-left bg-gray-200">Airflow</th>
                  {diag.points.map(p => cell(p, 'airflow'))}
                </tr>
                <tr>
                  <th className="px-2 py-1 border text-left bg-gray-200">Resistance</th>
                  {diag.points.map(p => cell(p, 'static_pressure'))}
                </tr>
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-xs text-muted-foreground">No pressure table in the read.</p>
        )}
      </div>
    );
  };

  // FS-674: what the expanded job offers, from the server's `recovery`
  // ONLY — a button when its entry is available, else why not and which
  // job or proposal to look at. Correct by hand needs the read (the detail
  // fetched on expand) to prefill from.
  const renderJobActions = (jobId: number) => {
    const job = jobs.find(j => j.id === jobId);
    if (!job) return null;
    const actions = recoveryActions(job);
    const detailState = jobDetails[jobId];
    const sameReason = !actions.retry.available && !actions.correct.available &&
      actions.retry.reason != null && actions.retry.reason === actions.correct.reason;
    const inFlight = retryBusy === jobId;
    return (
      <div className="mb-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs"
        data-testid={`job-actions-${jobId}`}>
        {actions.retry.available ? (
          <Button variant="secondary" size="sm" disabled={inFlight}
            onClick={e => { e.stopPropagation(); startRetry(job); }}
            title="Send this job's photos to the label reader again as a new import job. This job stays in the list.">
            {inFlight ? 'Retry read…' : 'Retry read'}
          </Button>
        ) : (actions.retry.reason && !sameReason) ? (
          <span className="text-gray-600">Retry read: {actions.retry.reason}</span>
        ) : null}
        {actions.correct.available ? (
          <Button variant="secondary" size="sm"
            disabled={inFlight || detailState?.state !== 'loaded'}
            onClick={e => { e.stopPropagation(); openCorrection(job); }}
            title="Open the manual entry form filled in with what the reader saw, to fix the values yourself and submit them as a proposal.">
            Correct by hand
          </Button>
        ) : (actions.correct.reason && !sameReason) ? (
          <span className="text-gray-600">Correct by hand: {actions.correct.reason}</span>
        ) : null}
        {sameReason && (
          <span className="text-gray-600">Retry read / Correct by hand: {actions.retry.reason}</span>
        )}
        {retryErrors[jobId] && (
          <span className="text-red-700" role="alert">{retryErrors[jobId]}</span>
        )}
      </div>
    );
  };

  const candidateCurve: CurvePoint[] = detail?.proposal?.candidate_curve ?? [];
  const pendingBadge = statusFilter === 'pending' && total > 0;
  const disclosureTitle = collapsed
    ? 'Show the label proposals queue and the photo import jobs.'
    : 'Hide the label proposals queue.';

  // The API returns origin-relative proxy paths; axiosInstance has no
  // baseURL, so resolve them against the catalog API base or the fetch
  // hits the SPA origin and gets index.html back.
  const photoProxyUrls = detailPhotoUrls;

  return (
    <Collapsible open={!collapsed} onOpenChange={(open) => setCollapsed(!open)} className="bg-card border border-border rounded-lg shadow-md p-4 mb-4 border-l-4 border-purple-300">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <CollapsibleTrigger render={<Button variant="secondary" title={disclosureTitle} />}
          className="flex items-center gap-2 whitespace-nowrap text-left"
          title={disclosureTitle}
        >
          <h3 className="text-lg font-bold">Label Proposals</h3>
          {pendingBadge && (
            <span className="px-2 py-0.5 rounded-full text-xs bg-purple-100 text-purple-800">
              {total} pending
            </span>
          )}
          <span className="text-muted-foreground text-sm">{collapsed ? '▸' : '▾'}</span>
        </CollapsibleTrigger>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="primary" onClick={openImport} className="whitespace-nowrap"
            title="Upload photos of a filter's label. The label reader turns them into a proposal for review.">
            Import photos
          </Button>
          <Button variant="secondary" onClick={openManual} className="whitespace-nowrap"
            title="Type a filter's identity and resistance curve by hand. It joins the queue as a proposal for review.">
            Enter manually
          </Button>
          {!collapsed && (
            <>
              {statusFilter !== 'pending' && (
                <label className="flex items-center gap-1 text-xs text-gray-600 select-none cursor-pointer">
                  <Checkbox checked={recentOnly} title="Limit this list to proposals from the last seven days."
                    onCheckedChange={setRecentOnly} />
                  Last 7 days
                </label>
              )}
              <label htmlFor="proposal-status" className="text-xs text-muted-foreground">Status</label>
              <Select value={statusFilter} onValueChange={(value) => { setStatusFilter(value ?? 'pending'); closeDetail(); }}>
                <SelectTrigger id="proposal-status" aria-describedby="proposal-status-help" title="Show pending, accepted or rejected proposals.">
                  <SelectValue>{statusFilter === 'pending' ? 'Pending' : statusFilter === 'accepted' ? 'Accepted' : 'Rejected'}</SelectValue>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="accepted">Accepted</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                </SelectContent>
              </Select>
              <span id="proposal-status-help" className="text-xs text-muted-foreground">Filter the review queue by status.</span>
            </>
          )}
        </div>
      </div>
      <p className="text-xs text-muted-foreground mt-1">
        Pressure-drop curves from user filter-label photos or manual admin
        entry (datasheet / printed label). A curve only reaches the live
        catalog after review and accept here.
      </p>

      {collapsed ? null : (
        <CollapsibleContent>
          {error && (
            <div className="border border-status-error-border bg-status-error text-status-error-foreground p-2 rounded-md my-2 text-sm">{error}</div>
          )}

          {/* Plan 109: manual-entry create form (explicit UI state; the
              proposal exists only after submit) */}
          {manual && (
            <div className="mt-3 border-2 border-purple-200 rounded-md p-3">
              {manualTarget ? (
                <>
                  <h4 className="text-md font-bold mb-2">
                    Correct import job {manualTarget.jobId} by hand
                  </h4>
                  <p className="text-xs text-muted-foreground mb-2">
                    Prefilled with what the model read; red cells are the
                    ones its read failed on. Fix the identity and the curve
                    against the photos. The job&apos;s photos stay attached as
                    evidence, and after review the curve publishes as
                    certified{' '}
                    <span className="px-1.5 py-0.5 rounded-sm text-[10px] border border-status-success-border bg-status-success text-status-success-foreground">filter_label</span>{' '}
                    once the accepting admin confirms the check against the photos.
                  </p>
                </>
              ) : (
                <>
                  <h4 className="text-md font-bold mb-2">Manual catalog entry</h4>
                  <p className="text-xs text-muted-foreground mb-2">
                    Transcribe identity and pressure curve from a datasheet or
                    printed label. Publishes as non-certified{' '}
                    <span className="px-1.5 py-0.5 rounded-sm text-[10px] border border-status-warning-border bg-status-warning text-status-warning-foreground">manual</span>{' '}
                    after review — never flips CFM calibration. For a family with
                    no data, transcribe a donor curve under the target identity
                    and name the donor in the note.
                  </p>
                </>
              )}
              <FilterIdentityCurveFields
                value={manual}
                onChange={editManualForm}
                curveFlags={curveFlags}
              />
              {manualCurveErrors.length > 0 && (manualTarget || manualCurveTouched) && (
                <p className="text-xs text-red-700 mb-2" data-testid="manual-curve-errors">
                  Fix the curve before submitting: {manualCurveErrors.join('; ')}
                </p>
              )}

              <label htmlFor="manual-source-note" className="block text-muted-foreground text-xs mb-0.5">
                Source note (datasheet / donor reference)
              </label>
              <Textarea id="manual-source-note" rows={2} value={manual.note}
                className="mb-2"
                aria-label="Source note"
                aria-describedby="manual-source-note-help"
                title="Name the datasheet or donor source used for this hand-entered proposal."
                onChange={e => setManualField('note', e.target.value)} />
              <p id="manual-source-note-help" className="mb-2 text-xs text-muted-foreground">Name the datasheet or donor curve used for this entry.</p>
              <ActionRow
                reason={manualCurveErrors.length > 0 && 'Enter a valid resistance curve to create the proposal.'}
                reasonId="manual-proposal-disabled-reason"
              >
                <Button variant="primary" onClick={submitManual} disabled={manualBusy || manualCurveErrors.length > 0}
                  aria-describedby={manualCurveErrors.length > 0 ? 'manual-proposal-disabled-reason' : undefined}
                  title="Add what you typed to the queue as a pending proposal. Nothing reaches the catalog until someone accepts it.">
                  {manualBusy ? 'Submitting…' : 'Create proposal'}
                </Button>
                <Button variant="secondary" onClick={closeManual}
                  title="Close this form without creating a proposal. What you typed is discarded.">
                  Cancel
                </Button>
              </ActionRow>
            </div>
          )}

          {/* Plan 109 Release B: photo-import intake form */}
          {importOpen && (
            <div className="mt-3 border-2 border-purple-200 rounded-md p-3">
              <h4 className="text-md font-bold mb-2">Import label photos</h4>
              <p className="text-xs text-muted-foreground mb-2">
                Up to {IMPORT_MAX_PHOTOS} photos of one filter's label or
                datasheet. Photos are resized locally, uploaded to private
                storage, and kept long-term as catalog evidence. Extraction
                usually finishes within a minute; a completed job links to
                its proposal for review.
              </p>
              <div
                className="border-2 border-dashed border-border rounded-md p-4 text-center text-sm text-muted-foreground mb-2"
                onDragOver={e => e.preventDefault()}
                onDrop={e => { e.preventDefault(); addImportFiles(e.dataTransfer.files); }}
              >
                Drag photos here, or{' '}
                <label className="text-link hover:text-blue-800 cursor-pointer underline">
                  browse
                  <Input type="file" accept="image/*" multiple className="hidden"
                    aria-label="Choose photos"
                    aria-describedby="import-photo-help"
                    title="Choose up to the allowed number of filter label photos for this import."
                    onChange={e => {
                      if (e.target.files) addImportFiles(e.target.files);
                      e.target.value = '';
                    }} />
                </label>
              </div>
              <p id="import-photo-help" className="text-xs text-muted-foreground">Choose photos of one filter label or datasheet.</p>
              {importFiles.length > 0 && (
                <ul className="text-xs text-gray-700 mb-2 space-y-1">
                  {importFiles.map((f, i) => (
                    <li key={`${f.name}-${i}`} className="flex items-center gap-2">
                      <span className="font-mono">{f.name}</span>
                      <span className="text-muted-foreground">
                        {(f.size / 1024 / 1024).toFixed(1)} MB
                      </span>
                      <Button variant="icon" onClick={() => removeImportFile(i)}
                        aria-label={`Remove photo ${i + 1}`}
                        title="Take this photo off the upload list. Nothing has been uploaded yet.">✕</Button>
                    </li>
                  ))}
                </ul>
              )}
              <label htmlFor="import-note" className="block text-muted-foreground text-xs mb-0.5">
                Note (filter identity, source of the sheet — optional)
              </label>
              <Textarea id="import-note" rows={2} value={importNote}
                className="mb-2"
                aria-label="Import note"
                aria-describedby="import-note-help"
                title="Add an optional note about the filter identity or source of the photos."
                onChange={e => setImportNote(e.target.value)} />
              <p id="import-note-help" className="mb-2 text-xs text-muted-foreground">Optional context for the reader and reviewer.</p>
              <ActionRow
                reason={importFiles.length === 0 && 'Choose at least one photo to upload.'}
                reasonId="import-disabled-reason"
              >
                <Button variant="primary" onClick={submitImport}
                  disabled={importBusy || importFiles.length === 0}
                  aria-describedby={importFiles.length === 0 ? 'import-disabled-reason' : undefined}
                  title="Upload these photos as one import job. The label reader reads them and adds a proposal to the queue.">
                  {importBusy ? 'Uploading…' : `Upload ${importFiles.length || ''} photo${importFiles.length === 1 ? '' : 's'}`}
                </Button>
                <Button variant="secondary" onClick={closeImport}
                  title="Close the import form without uploading anything.">
                  Cancel
                </Button>
              </ActionRow>
            </div>
          )}

          {/* Photo-import jobs (intake shipped; extraction pending) */}
          {jobs.length > 0 && (
            <div className="mt-3">
              <h4 className="text-sm font-bold mb-1">Photo imports</h4>
              {pollStalled && hasActiveJobs && (
                <p className="text-xs text-amber-700 mb-1">
                  Auto-refresh paused — jobs haven't changed in 5 minutes;
                  the pipeline may be stuck.{' '}
                  <Button variant="link" onClick={resumePolling}
                    title="Check the import jobs and proposals again now, and turn automatic refresh back on.">
                    Refresh
                  </Button>
                </p>
              )}
              <div className="overflow-x-auto">
                <table className="table-auto w-full text-left border-collapse border border-border text-xs">
                  <thead>
                    <tr className="bg-gray-200">
                      <th className="px-2 py-1.5 border">ID</th>
                      <th className="px-2 py-1.5 border">Status</th>
                      <th className="px-2 py-1.5 border">Photos</th>
                      <th className="px-2 py-1.5 border">Submitted</th>
                      <th className="px-2 py-1.5 border">Submitter</th>
                      <th className="px-2 py-1.5 border">Note</th>
                      <th className="px-2 py-1.5 border">Proposal</th>
                    </tr>
                  </thead>
                  <tbody>
                    {jobs.map(j => (
                      <tr key={j.id} className="hover:bg-purple-50 cursor-pointer"
                        onClick={() => toggleJobPhotos(j)}>
                        <td className="px-2 py-1 border">{j.id}</td>
                        <td className="px-2 py-1 border">
                          <span className={`px-1.5 py-0.5 rounded-sm text-[10px] ${JOB_STATUS_BADGE[j.status] ?? 'border border-status-neutral-border bg-status-neutral text-status-neutral-foreground'}`}>
                            {j.status}
                          </span>
                          {/* FS-674 lineage: what this job re-read, and what re-read it. */}
                          {j.retry_of != null && (
                            <span className="ml-1 text-muted-foreground">retry of {j.retry_of}</span>
                          )}
                          {j.retried_by && (
                            <span className="ml-1 text-muted-foreground">retried as {j.retried_by.id}</span>
                          )}
                          {j.error_detail && (
                            <span className="ml-1 text-status-error-foreground">{j.error_detail}</span>
                          )}
                        </td>
                        <td className="px-2 py-1 border text-center">{j.photo_count}</td>
                        <td className="px-2 py-1 border">{new Date(j.created_at).toLocaleDateString()}</td>
                        <td className="px-2 py-1 border">
                          <SubmitterName uid={j.submitting_uid} email={j.submitting_email} />
                        </td>
                        <td className="px-2 py-1 border">{j.note ?? '-'}</td>
                        <td className="px-2 py-1 border">
                          {j.proposal_id != null ? (
                            <Button
                              variant="link"
                              title="Open the proposal this import produced, to review it and accept or reject it."
                              onClick={e => { e.stopPropagation(); openDetail(j.proposal_id as number); }}>
                              Review #{j.proposal_id}
                            </Button>
                          ) : '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {jobPhotos && (
                <div className="mt-2">
                  {renderJobRead(jobPhotos.id)}
                  {renderJobActions(jobPhotos.id)}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                    {jobPhotos.urls.map((u, i) => (
                      <div key={u} className="border rounded-md overflow-hidden bg-gray-50">
                        <AuthedImage url={u} alt={`Import ${jobPhotos.id} photo ${i + 1}`} />
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Summary list */}
          <div className="overflow-x-auto mt-3">
            <table className="table-auto w-full text-left border-collapse border border-border text-xs">
              <thead>
                <tr className="bg-gray-200">
                  <th className="px-2 py-1.5 border">ID</th>
                  <th className="px-2 py-1.5 border">Origin</th>
                  <th className="px-2 py-1.5 border">Submitter</th>
                  <th className="px-2 py-1.5 border">Created</th>
                  <th className="px-2 py-1.5 border">Brand</th>
                  <th className="px-2 py-1.5 border">Size</th>
                  <th className="px-2 py-1.5 border">MERV</th>
                  <th className="px-2 py-1.5 border">Device</th>
                  <th className="px-2 py-1.5 border">Photos</th>
                  <th className="px-2 py-1.5 border">Points</th>
                  <th className="px-2 py-1.5 border">Sanity</th>
                  {statusFilter !== 'pending' && <th className="px-2 py-1.5 border">Reviewer</th>}
                </tr>
              </thead>
              <tbody>
                {proposals.map(p => (
                  <tr key={p.id}
                    className={`hover:bg-purple-50 cursor-pointer ${detail?.proposal?.id === p.id ? 'bg-purple-50' : ''}`}
                    onClick={() => openDetail(p.id)}>
                    <td className="px-2 py-1 border">
                      <Button variant="link" size="sm" title={`Open proposal ${p.id} for review.`}
                        onClick={event => { event.stopPropagation(); openDetail(p.id); }}>
                        {p.id}
                      </Button>
                    </td>
                    <td className="px-2 py-1 border">
                      <span className={`px-1.5 py-0.5 rounded-sm text-[10px] ${originBadgeClass(p.origin)}`}>
                        {originLabel(p.origin)}
                      </span>
                    </td>
                    <td className="px-2 py-1 border">
                      <SubmitterName uid={p.submitting_uid} email={p.submitting_email} />
                    </td>
                    <td className="px-2 py-1 border">{new Date(p.created_at).toLocaleDateString()}</td>
                    <td className="px-2 py-1 border">{p.candidate_brand ?? '-'}</td>
                    <td className="px-2 py-1 border">{p.candidate_nominal_size ?? '-'}</td>
                    <td className="px-2 py-1 border text-center">{p.candidate_merv ?? '-'}</td>
                    <td className="px-2 py-1 border font-mono">{p.device_id ?? p.device_name ?? '-'}</td>
                    <td className="px-2 py-1 border text-center">{p.photo_count}</td>
                    <td className="px-2 py-1 border text-center">{p.point_count}</td>
                    <td className="px-2 py-1 border">
                      {p.sanity && p.sanity.errors.length > 0 && (
                        <span className="px-1.5 py-0.5 rounded-sm text-[10px] border border-status-error-border bg-status-error text-status-error-foreground">
                          {p.sanity.errors.length} error{p.sanity.errors.length !== 1 ? 's' : ''}
                        </span>
                      )}
                      {p.sanity && p.sanity.errors.length === 0 && p.sanity.warnings.length > 0 && (
                        <span className="px-1.5 py-0.5 rounded-sm text-[10px] border border-status-warning-border bg-status-warning text-status-warning-foreground">
                          {p.sanity.warnings.length} warning{p.sanity.warnings.length !== 1 ? 's' : ''}
                        </span>
                      )}
                      {p.sanity && p.sanity.errors.length === 0 && p.sanity.warnings.length === 0 && (
                        <span className="px-1.5 py-0.5 rounded-sm text-[10px] border border-status-success-border bg-status-success text-status-success-foreground">ok</span>
                      )}
                    </td>
                    {statusFilter !== 'pending' && (
                      <td className="px-2 py-1 border">{p.reviewer ?? '-'}</td>
                    )}
                  </tr>
                ))}
                {proposals.length === 0 && (
                  <tr>
                    <td colSpan={12} className="px-2 py-4 border text-center text-muted-foreground">
                      No {statusFilter} proposals
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Detail: photo beside the parsed curve */}
          {detail && form && (
            <div className="mt-4 border-t pt-4">
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-md font-bold">
                  Proposal #{detail.proposal.id}
                  <span className={`ml-2 px-1.5 py-0.5 rounded-sm text-[10px] font-normal ${originBadgeClass(detail.proposal.origin)}`}>
                    {originLabel(detail.proposal.origin)}
                  </span>
                  <span className="ml-2 text-xs font-normal text-muted-foreground">
                    from{' '}
                    <SubmitterName
                      uid={detail.proposal.submitting_uid}
                      email={detail.proposal.submitting_email}
                    />
                    {detail.proposal.device_id ? ` · device ${detail.proposal.device_id}` : ''}
                    {' · '}{detail.proposal.model} v{detail.proposal.prompt_version}
                  </span>
                  {/* FS-674: structural provenance — the job whose proposal_id
                      points here. */}
                  {detail.import_job && (
                    <span className="ml-2 text-xs font-normal text-muted-foreground">
                      · {detail.proposal.origin === 'admin_corrected' ? 'corrected from' : 'read from'}{' '}
                      import job {detail.import_job.id}
                    </span>
                  )}
                </h4>
                <Button variant="secondary" onClick={closeDetail}
                  title="Close this proposal without accepting or rejecting it. It stays in the queue.">
                  Close
                </Button>
              </div>

              {/* Sanity */}
              {(detail.sanity.errors.length > 0 || detail.sanity.warnings.length > 0) && (
                <div className="mb-3 text-xs space-y-1">
                  {detail.sanity.errors.map((e, i) => (
                    <div key={`e${i}`} className="border border-status-error-border bg-status-error text-status-error-foreground px-2 py-1 rounded-sm">✕ {e}</div>
                  ))}
                  {detail.sanity.warnings.map((w, i) => (
                    <div key={`w${i}`} className="border border-status-warning-border bg-status-warning text-status-warning-foreground px-2 py-1 rounded-sm">⚠ {w}</div>
                  ))}
                </div>
              )}

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* Photos */}
                <div>
                  <h5 className="text-sm font-bold mb-2">
                    Source photo{photoProxyUrls.length !== 1 ? 's' : ''}
                  </h5>
                  <div className="space-y-3">
                    {photoProxyUrls.map((u, i) => (
                      <div key={u} className="border rounded-md overflow-hidden bg-gray-50">
                        <AuthedImage url={u} alt={`Label photo ${i + 1}`}
                          onSettled={state => onDetailPhoto(u, state)} />
                      </div>
                    ))}
                    {photoProxyUrls.length === 0 && (
                      <div className="text-sm text-muted-foreground">No photos on this proposal</div>
                    )}
                  </div>
                </div>

                {/* Curve + review form */}
                <div>
                  <h5 className="text-sm font-bold mb-2">Reviewed identity + curve</h5>
                  <FilterIdentityCurveFields
                    value={form}
                    onChange={editReviewForm}
                    showManufacturer
                    ariaPrefix="Reviewed "
                    fieldErrors={fieldErrors}
                    disabled={busy}
                    brandSlot={detail.proposal.status === 'pending' &&
                      freshResult && !freshResult.brand_known ? (
                        <span className="block text-[11px]">
                          <span className="block text-status-error-foreground font-semibold">
                            New brand — not in the catalog
                          </span>
                          {freshResult.brand_suggestions.length > 0 && (
                            <span className="block text-gray-600">
                              Did you mean:{' '}
                              {freshResult.brand_suggestions.map(s => (
                                <Button key={s.brand_canon} variant="link" disabled={busy}
                                  title={`${s.sample_model} · ${s.row_count} catalog row${s.row_count === 1 ? '' : 's'}`}
                                  className="mr-2"
                                  onClick={() => replaceIdentityField('brand', s.brand)}>
                                  {s.brand}
                                </Button>
                              ))}
                            </span>
                          )}
                          {resolution?.action !== 'update_existing' && (
                            <label className="flex items-center gap-1 text-gray-800 mt-0.5 cursor-pointer">
                              <Checkbox checked={confirmNewBrand} disabled={busy}
                                aria-label={`Add ${form.brand.trim()} as a new brand`}
                                title={`Add ${form.brand.trim()} as a new catalog brand when this proposal is accepted.`}
                                onCheckedChange={setConfirmNewBrand} />
                              <span>Add <em>{form.brand.trim()}</em> as a new brand</span>
                            </label>
                          )}
                        </span>
                      ) : null}
                  />
                  {otherIdentityErrors.length > 0 && (
                    <div className="mb-2 text-xs text-red-700" role="alert">
                      {otherIdentityErrors.map(e => <div key={e}>{e}</div>)}
                    </div>
                  )}

                  {/* Same-family area-scaled comparison */}
                  {detail.family_comparisons.length > 0 && (
                    <div className="mb-3">
                      <h5 className="text-sm font-bold mb-1">Same-family comparison (area-scaled)</h5>
                      <p className="text-[11px] text-muted-foreground mb-1">
                        MAEDbS curves for this brand/depth/MERV at other sizes,
                        scaled to this size. Close agreement = strong evidence
                        the read is correct.
                      </p>
                      {detail.family_comparisons.map(cmp => (
                        <div key={cmp.catalog_id} className="text-xs mb-1">
                          <span className="font-mono">{cmp.model}</span>
                          {' '}({cmp.nominal_size}
                          {cmp.area_scale_factor != null ? ` × ${cmp.area_scale_factor.toFixed(2)}` : ''}):{' '}
                          {cmp.scaled_curve
                            ? cmp.scaled_curve.map(p => `${p.cfm}@${p.resistance_iwc}`).join(', ')
                            : 'not scalable'}
                        </div>
                      ))}
                      {candidateCurve.length > 0 && (
                        <div className="text-xs text-gray-700">
                          <span className="font-semibold">This label:</span>{' '}
                          {candidateCurve.map(p => `${p.cfm}@${p.resistance_iwc}`).join(', ')}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Diff against the row being overridden */}
                  {detail.current_catalog_row && (
                    <div className="mb-3 text-xs bg-amber-50 border border-status-warning-border rounded-sm p-2">
                      <span className="font-semibold">Overrides existing row #{detail.current_catalog_row.id}</span>
                      {' '}({detail.current_catalog_row.source}):{' '}
                      {(detail.current_catalog_row.dp_curve ?? [])
                        .map((p: [number, number]) => `${p[1]}@${p[0]}`).join(', ') || 'no curve'}
                    </div>
                  )}

                  {/* Publication preview */}
                  {detail.publication_preview && (
                    <div className="mb-3 text-xs bg-gray-50 border rounded-sm p-2">
                      <div className="font-semibold mb-1">
                        Publication preview —{' '}
                        {detail.publication_preview.would_create
                          ? 'creates a new catalog row'
                          : 'updates the existing row'}{' '}
                        {detail.proposal.origin === 'admin_manual'
                          ? <span className="px-1.5 py-0.5 rounded-sm text-[10px] border border-status-warning-border bg-status-warning text-status-warning-foreground">manual</span>
                          : <span className="px-1.5 py-0.5 rounded-sm text-[10px] border border-status-success-border bg-status-success text-status-success-foreground">filter_label</span>}
                      </div>
                      <div className="font-mono">
                        {String(detail.publication_preview.row.model)}
                      </div>
                      <div className="mt-1">
                        ΔP@400: {detail.publication_preview.derived.dp_at_400cfm ?? '—'}
                        {' · '}@800: {detail.publication_preview.derived.dp_at_800cfm ?? '—'}
                        {' · '}@1200: {detail.publication_preview.derived.dp_at_1200cfm ?? '—'}
                        {' · '}@1600: {detail.publication_preview.derived.dp_at_1600cfm ?? '—'}
                        {' · '}max CFM: {detail.publication_preview.derived.max_rated_cfm}
                        {' · '}C: {detail.publication_preview.derived.c_coefficient}
                      </div>
                      <div className="text-muted-foreground mt-1">
                        Preview reflects the submitted candidate; edited values
                        are re-validated and re-derived on accept.
                      </div>
                    </div>
                  )}

                  {/* Submitter's source note — read-only context, never the
                      review/rejection note. Accept preserves it server-side
                      unless the reviewer writes a new one. */}
                  {detail.proposal.source_note && (
                    <div className="mb-2 text-xs bg-gray-50 border rounded-sm p-2">
                      <span className="font-semibold">Submitter note:</span>{' '}
                      {detail.proposal.source_note}
                    </div>
                  )}

                  {detail.proposal.status === 'pending' && (() => {
                    const typedMaedb = collapseNumber(form.maedb_model_number);
                    // FS-663: the server's verdict forces the selection —
                    // never number equality, so two cross-brand rows
                    // sharing a number both stay selectable.
                    const exactSibling = conflicts.find(s => s.exact_identity);
                    const selectable = conflictsFresh && !busy;
                    const addSeparateAllowed = typedMaedb !== '' &&
                      exactSibling == null &&
                      conflicts.every(s => collapseNumber(s.maedb_model_number) !== typedMaedb);
                    const hasCrossBrand = conflicts.some(s => s.match_reason === 'model_number');
                    return (
                      <>
                        {conflicts.length > 0 && (
                          <div className={`mb-3 text-xs border border-status-warning-border bg-amber-50 rounded-sm p-2 ${
                            conflictsFresh ? '' : 'opacity-60'}`}>
                            <div className="font-semibold text-amber-900 mb-1">
                              This filter may already be in the catalog.
                            </div>
                            <p className="mb-2 text-amber-900">
                              The catalog holds {conflicts.length === 1
                                ? 'an entry' : `${conflicts.length} entries`}{' '}
                              {hasCrossBrand
                                ? 'for the same size, depth and MERV — under this brand, or sharing a model number under another brand name.'
                                : 'for the same brand, size and MERV under a different MAEDbS model number.'}{' '}
                              Choose how to publish this proposal:
                            </p>
                            {exactSibling && (
                              <p className="mb-2 font-semibold text-amber-900">
                                Entry #{exactSibling.id} is an exact match for
                                the reviewed identity — accepting updates that
                                entry.
                              </p>
                            )}
                            <RadioGroup
                              value={resolution?.action === 'update_existing' ? `update:${resolution.catalog_id}` : resolution?.action === 'create_new' ? 'create' : ''}
                              onValueChange={(value) => {
                                if (value === 'create') {
                                  setResolution({ action: 'create_new' });
                                  return;
                                }
                                const selected = conflicts.find((entry) => `update:${entry.id}` === value);
                                if (selected) setResolution({ action: 'update_existing', catalog_id: selected.id });
                              }}
                              aria-label="How to publish this proposal"
                            >
                            {conflicts.map(s => {
                              const why = siblingMatch(s);
                              const disabled = !selectable ||
                                (exactSibling != null && exactSibling.id !== s.id);
                              return (
                                <label key={s.id} className={`flex items-start gap-2 mb-1.5 ${
                                  disabled ? 'opacity-60' : 'cursor-pointer'}`}>
                                  <RadioGroupItem value={`update:${s.id}`} className="mt-0.5"
                                    disabled={disabled}
                                    title={`Update catalog entry ${s.id} with this proposal's curve.`} />
                                  <span>
                                    <span className="font-semibold">Update entry #{s.id}</span>
                                    {' '}— replace its curve with this proposal&apos;s.{' '}
                                    <span className="font-mono">{s.model}</span>
                                    {' · '}{s.brand}
                                    {' · '}{s.maedb_model_number
                                      ? `MAEDbS ${s.maedb_model_number}` : 'no MAEDbS number'}
                                    {' · '}{s.source}
                                    {s.certified && (
                                      <span className="ml-1 px-1 py-0.5 rounded-sm text-[10px] border border-status-success-border bg-status-success text-status-success-foreground">certified</span>
                                    )}
                                    {' · '}{s.curve_point_count}-point curve
                                    {s.source_date
                                      ? ` · published ${String(s.source_date).slice(0, 10)}` : ''}
                                    {(why.kind === 'exact' || why.kind === 'model_number') && (
                                      <span className="block text-amber-800" data-testid={`sibling-match-${s.id}`}>
                                        {why.kind === 'model_number' ? (
                                          <>same model number <em>{why.token}</em> under a
                                            different brand name (<strong>{why.brand}</strong>)</>
                                        ) : why.text}
                                      </span>
                                    )}
                                    {s.certified && detail.proposal.origin === 'admin_manual' && (
                                      <span className="block text-amber-700">
                                        Replaces a certified curve with
                                        hand-entered data — the result is never
                                        used for CFM calibration.
                                      </span>
                                    )}
                                  </span>
                                </label>
                              );
                            })}
                            <label className={`flex items-start gap-2 mb-1 ${addSeparateAllowed && selectable
                              ? 'cursor-pointer' : 'opacity-60'}`}>
                              <RadioGroupItem value="create" className="mt-0.5"
                                disabled={!addSeparateAllowed || !selectable}
                                title="Publish this proposal as a separate catalog product." />
                              <span>
                                <span className="font-semibold">Add as a separate product</span>
                                {' '}— a genuinely different filter that shares
                                this size, depth and MERV.
                                {!addSeparateAllowed && (
                                  <span className="block text-amber-700">
                                    Requires a MAEDbS model number (above)
                                    different from every entry listed here.
                                  </span>
                                )}
                              </span>
                            </label>
                            </RadioGroup>
                            <p className="text-amber-800 mt-1">
                              Or Reject below to leave the catalog unchanged.
                            </p>
                          </div>
                        )}

                        {/* FS-663 (F2): an accept also decides what the
                            brand spellings mean from now on — the submitted
                            one as much as the reviewed one, whatever found
                            the target. Show the alias it records, checked,
                            so the consequence is visible and can be
                            declined. Hidden when nothing would be written. */}
                        {aliasesPending.length > 0 && (
                          <label className="flex items-start gap-2 mb-3 text-xs text-amber-900 border border-status-warning-border bg-amber-50 rounded-sm p-2 cursor-pointer">
                            <Checkbox className="mt-0.5" checked={recordAlias}
                              disabled={busy}
                              aria-label={aliasConsentLabel(aliasesPending.map(a => a.spelling), finalBrand)}
                              title="Record the listed brand spellings as aliases when accepting this proposal."
                              onCheckedChange={setRecordAlias} />
                            <span>
                              {aliasConsentLabel(aliasesPending.map(a => a.spelling), finalBrand)}
                            </span>
                          </label>
                        )}

                        {/* FS-663 409 identity_taken: another row already
                            holds the identity the accept would publish. */}
                        {identityTaken && (
                          <div className="mb-3 text-xs border border-status-error-border bg-red-50 rounded-sm p-2 text-red-900"
                            data-testid="identity-taken">
                            <div className="font-semibold mb-1">
                              Entry #{identityTaken.id} already holds this exact identity.
                            </div>
                            <div className="mb-2">
                              <span className="font-mono">{identityTaken.model}</span>
                              {' · '}{identityTaken.brand}
                              {' · '}{identityTaken.maedb_model_number
                                ? `MAEDbS ${identityTaken.maedb_model_number}` : 'no MAEDbS number'}
                              {' · '}{identityTaken.source}
                            </div>
                            <div className="flex flex-wrap gap-2">
                              <Button variant="secondary" size="sm" onClick={chooseTakenRow}
                                title={`Make Accept update entry #${identityTaken.id}, the row that already holds this identity, instead of adding a new one.`}>
                                Update entry #{identityTaken.id} instead
                              </Button>
                              <Button variant="secondary" size="sm"
                                onClick={() => replaceIdentityField('maedb_model_number', '')}
                                title={`Empty the MAEDbS number on this proposal so it no longer collides with entry #${identityTaken.id}.`}>
                                Clear the MAEDbS number
                              </Button>
                            </div>
                          </div>
                        )}

                        {/* FS-663 409 alias_conflict: the spelling already
                            maps to another brand. */}
                        {aliasConflict && (
                          <div className="mb-3 text-xs border border-status-error-border bg-red-50 rounded-sm p-2 text-red-900"
                            data-testid="alias-conflict">
                            <div className="font-semibold mb-1">
                              The spelling &apos;{aliasConflict.alias_key}&apos; ({aliasConflict.origin} brand)
                              already maps to <span className="font-mono">{aliasConflict.existing_canon}</span>,
                              not <span className="font-mono">{aliasConflict.final_canon}</span>.
                            </div>
                            <p className="mb-2">
                              Redirecting an existing alias is a separate,
                              reviewed change. You can still publish this
                              proposal without recording the alias.
                            </p>
                            <Button variant="secondary" size="sm" onClick={() => accept(false)}
                              disabled={!acceptEnabled}
                              title="Publish this proposal now and leave the brand spelling mapped where it is. Redirecting that alias is a separate, reviewed change.">
                              Accept without recording the alias
                            </Button>
                          </div>
                        )}

                        <label htmlFor="proposal-review-note" className="block text-muted-foreground text-xs mb-0.5">
                          Review note (required to reject)
                        </label>
                        <Textarea id="proposal-review-note" rows={2} value={form.note}
                          className="mb-2"
                          aria-describedby="proposal-review-note-help"
                          title="Explain the review decision; a note is required to reject."
                          onChange={e => setFormField('note', e.target.value)} />
                        <p id="proposal-review-note-help" className="mb-2 text-xs text-muted-foreground">A reason is required when rejecting this proposal.</p>

                        {/* FS-663: Accept waits for the catalog's answer
                            about the identity as it reads right now. */}
                        {check?.status === 'error' ? (
                          <p className="text-xs text-red-700 mb-1" role="alert">
                            Couldn&apos;t check the catalog for this identity.{' '}
                            <Button variant="link"
                              onClick={() => currentIdentity && currentKey != null &&
                                runIdentityCheck(detail.proposal.id, currentIdentity, currentKey)}
                              title="Ask the catalog again whether an entry already holds this identity. Accept waits for the answer.">
                              Retry check
                            </Button>
                          </p>
                        ) : freshResult == null ? (
                          <p className="text-xs text-muted-foreground mb-1">
                            Checking the catalog for this identity…
                          </p>
                        ) : identityErrorEntries.length > 0 ? (
                          <p className="text-xs text-red-700 mb-1">
                            Fix the identity fields marked above before accepting.
                          </p>
                        ) : null}
                        {/* FS-674 §3: on a corrected proposal, publishing as a
                            certified curve is the accepting admin's stated
                            check against the photos — it waits for every
                            photo of this proposal to load, and any identity
                            or curve edit unticks it. */}
                        {attestationRequired && (
                          <label className="flex items-start gap-2 mb-2 text-xs border border-status-success-border bg-green-50 rounded-sm p-2 text-green-900 cursor-pointer">
                            <Checkbox className="mt-0.5"
                              aria-label="I checked the identity and every curve value against the photos"
                              checked={verifiedAgainstPhotos}
                              disabled={busy || anyPhotoFailed || !allPhotosLoaded}
                              title="Attest that you checked the identity and each curve value against all source photos."
                              onCheckedChange={setVerifiedAgainstPhotos} />
                            <span>
                              I checked the identity and every curve value against the photos
                              {anyPhotoFailed ? (
                                <span className="block text-red-700">
                                  A photo failed to load — reload it before attesting.
                                </span>
                              ) : !allPhotosLoaded ? (
                                <span className="block text-muted-foreground">
                                  Waiting for every photo to load…
                                </span>
                              ) : null}
                            </span>
                          </label>
                        )}
                        <ActionRow
                          reason={acceptDisabledReason}
                          reasonId="proposal-accept-disabled-reason"
                        >
                          <Button variant="primary" onClick={() => accept()} disabled={!acceptEnabled}
                            aria-describedby={acceptDisabledReason ? 'proposal-accept-disabled-reason' : undefined}
                            title="Publish the reviewed values to the filter catalog. The button says whether that adds an entry or updates an existing one.">
                            {busy ? 'Working…'
                              : resolution?.action === 'update_existing'
                                ? `Accept → update entry #${resolution.catalog_id}`
                                : resolution?.action === 'create_new'
                                  ? 'Accept → add separate product'
                                  : 'Accept → publish'}
                          </Button>
                          <Button variant="danger" onClick={reject} disabled={busy}
                            title="Reject this proposal with the review note above (required). Nothing reaches the catalog.">
                            Reject
                          </Button>

                        </ActionRow>
                      </>
                    );
                  })()}
                  {detail.proposal.status !== 'pending' && (
                    <div className="text-xs text-muted-foreground">
                      {detail.proposal.status} by {detail.proposal.reviewer}
                      {detail.proposal.reviewed_at
                        ? ` on ${new Date(detail.proposal.reviewed_at).toLocaleString()}` : ''}
                      {detail.proposal.reject_reason ? ` — ${detail.proposal.reject_reason}` : ''}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </CollapsibleContent>
      )}
    </Collapsible>
  );
};

export default LabelProposalsPanel;
